import React, { useEffect, useState } from "react";
import Tooltip from "./Tooltip";
import ViolationTrendChart from "./ViolationTrendChart";
import { ViolationTrendPoint } from "../types";
import "./VehicleDetailPanel.css";

export interface VehicleDetailContext {
  plateNumber: string;
  violationId: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  evidenceURL: string | null;
  plateConfidence: number | null;
  vehicleConfidence: number | null;
  lastSeenLocation: string | null;
  // ISO timestamp of the violation currently in view; rendered next to the lot name.
  timestamp: string | null;
  notes: string;
  // True if validation is allowed for this lot AND the plate is currently in the lot.
  canValidate: boolean;
  isPinned: boolean;
}

interface VehicleDetailPanelProps {
  context: VehicleDetailContext | null;
  trend: ViolationTrendPoint[] | null;
  trendLoading: boolean;
  onClear: () => void;
  onPrint: () => void;
  onValidate: () => void;
  onAddToRegistry: () => void;
  registryAdded: boolean;
  onSaveNotes: (notes: string) => Promise<void> | void;
  // Optional quick-scrub state: shows "Violation N of M" with arrows above the evidence image.
  scrub?: { index: number; total: number; onPrev: () => void; onNext: () => void } | null;
}

const formatConfidence = (n: number | null) => {
  if (n === null || n === undefined) return "—";
  // ALPR values appear in 0..1 or 0..100. Normalize.
  const pct = n > 1 ? n : n * 100;
  return `${Math.round(pct)}%`;
};

const VehicleDetailPanel: React.FC<VehicleDetailPanelProps> = ({
  context,
  trend,
  trendLoading,
  onClear,
  onPrint,
  onValidate,
  onAddToRegistry,
  registryAdded,
  onSaveNotes,
  scrub,
}) => {
  const [editingNotes, setEditingNotes] = useState(false);
  const [draftNotes, setDraftNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Reset editor whenever the displayed violation changes.
  useEffect(() => {
    setEditingNotes(false);
    setDraftNotes(context?.notes || "");
  }, [context?.violationId, context?.notes]);

  if (!context) {
    // Skeleton placeholder so the panel reads as a real component, not an empty card.
    return (
      <aside className="vehicle-detail-panel vehicle-detail-skeleton">
        <div className="vehicle-detail-image vehicle-detail-image-prompt">
          <span>Hover or click to view</span>
        </div>
        <div className="vehicle-detail-skeleton-row">
          <div className="skeleton-block skeleton-plate" />
          <div className="vehicle-detail-skeleton-stack">
            <div className="skeleton-block skeleton-line" />
            <div className="skeleton-block skeleton-line short" />
          </div>
        </div>
        <div className="skeleton-block skeleton-bar" />
        <div className="skeleton-block skeleton-bar" />
        <div className="skeleton-block skeleton-notes" />
        <div className="vehicle-detail-skeleton-actions">
          <div className="skeleton-block skeleton-button" />
          <div className="skeleton-block skeleton-button" />
          <div className="skeleton-block skeleton-button" />
          <div className="skeleton-block skeleton-button" />
        </div>
      </aside>
    );
  }

  return (
    <aside className={`vehicle-detail-panel ${context.isPinned ? "pinned" : ""}`}>
      {scrub && (
        <div className="vehicle-detail-scrub">
          <button
            type="button"
            className="scrub-arrow"
            onClick={scrub.onPrev}
            disabled={scrub.total <= 1}
            aria-label="Previous violation"
          >
            <img src="/assets/violations/ArrowLeft.svg" alt="" />
          </button>
          <span>
            Violation {scrub.index + 1} of {scrub.total}
          </span>
          <button
            type="button"
            className="scrub-arrow"
            onClick={scrub.onNext}
            disabled={scrub.total <= 1}
            aria-label="Next violation"
          >
            <img src="/assets/violations/ArrowRight.svg" alt="" />
          </button>
        </div>
      )}

      <div className="vehicle-detail-image">
        {context.evidenceURL ? (
          <img src={context.evidenceURL} alt={`Vehicle ${context.plateNumber}`} />
        ) : (
          <div className="vehicle-detail-image-placeholder">No image found</div>
        )}
      </div>

      <div className="vehicle-detail-summary">
        <div className="plate-badge vehicle-detail-plate">{context.plateNumber}</div>
        <div className="vehicle-detail-vehicle">
          <div className="vehicle-detail-line">
            {[context.vehicleMake, context.vehicleModel].filter(Boolean).join(" ") || "Unknown vehicle"}
          </div>
          {context.vehicleColor && <div className="vehicle-detail-line muted">{context.vehicleColor}</div>}
        </div>
      </div>

      {(context.plateConfidence !== null || context.vehicleConfidence !== null) && (
        <div className="vehicle-detail-confidence">
          <span className="vehicle-detail-label">Confidence</span>
          <span className="vehicle-detail-confidence-values">
            <span>{formatConfidence(context.plateConfidence)}</span>
            <span>{formatConfidence(context.vehicleConfidence)}</span>
          </span>
        </div>
      )}

      <div className="vehicle-detail-location">
        <span className="vehicle-detail-location-value">
          {context.lastSeenLocation || "—"}
          {context.timestamp && (
            <>
              {" · "}
              {new Date(context.timestamp).toLocaleString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </>
          )}
        </span>
      </div>

      <div className="vehicle-detail-notes">
        <div className="vehicle-detail-notes-header">
          <span className="vehicle-detail-label">Notes</span>
          {!editingNotes && context.violationId && (
            <button
              type="button"
              className="vehicle-detail-notes-edit"
              onClick={() => setEditingNotes(true)}
              aria-label="Edit notes"
              title="Edit notes"
            >
              <img src="/assets/Edit.svg" alt="" />
            </button>
          )}
        </div>
        {editingNotes ? (
          <>
            <textarea
              className="vehicle-detail-notes-textarea"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              maxLength={4000}
              rows={3}
              autoFocus
            />
            <div className="vehicle-detail-notes-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  setEditingNotes(false);
                  setDraftNotes(context.notes || "");
                }}
                disabled={savingNotes}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={async () => {
                  setSavingNotes(true);
                  try {
                    await onSaveNotes(draftNotes);
                    setEditingNotes(false);
                  } finally {
                    setSavingNotes(false);
                  }
                }}
                disabled={savingNotes}
              >
                {savingNotes ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        ) : (
          <div className="vehicle-detail-notes-body">
            {context.notes ? context.notes : <span className="vehicle-detail-notes-empty">No notes yet.</span>}
          </div>
        )}
      </div>

      <div className="vehicle-detail-actions">
        <button className="button-danger" onClick={onClear}>
          <img src="/assets/violations/clear.svg" alt="" aria-hidden="true" className="action-icon" />
          Clear
        </button>
        <button className="button-primary" onClick={onPrint}>
          <img src="/assets/violations/print.svg" alt="" aria-hidden="true" className="action-icon" />
          Print
        </button>
        <Tooltip
          text={context.canValidate ? "Validate this session" : "Validation unavailable (off for this lot or vehicle has departed)"}
          position="left"
          size="small"
          delayMs={400}
        >
          <button
            className="button-secondary"
            onClick={onValidate}
            disabled={!context.canValidate}
          >
            <img src="/assets/violations/validate.svg" alt="" aria-hidden="true" className="action-icon" />
            Validate
          </button>
        </Tooltip>
        <button
          className={`button-secondary ${registryAdded ? "registry-added" : ""}`}
          onClick={onAddToRegistry}
          disabled={registryAdded}
        >
          {registryAdded ? (
            <span className="registry-added-label">
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M3 8.5L6.5 12L13 4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Added
            </span>
          ) : (
            <>
              <img src="/assets/violations/add_to_registry.svg" alt="" aria-hidden="true" className="action-icon" />
              Registry
            </>
          )}
        </button>
      </div>

      <ViolationTrendChart data={trend} loading={trendLoading} />
    </aside>
  );
};

export default VehicleDetailPanel;
